import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import Sparkline from './Sparkline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SKILLS = [
  { key: 'discovery', label: 'Discovery', color: '#3B82F6', avgKey: 'avg_discovery_quality' },
  { key: 'objection_handling', label: 'Objection Handling', color: '#F59E0B', avgKey: 'avg_objection_handling' },
  { key: 'closing', label: 'Closing / Next Steps', color: '#EF4444', avgKey: 'avg_next_step_clarity' },
  { key: 'deal_score', label: 'Deal Score', color: '#10B981', avgKey: null },
  { key: 'talk_ratio', label: 'Talk Ratio', color: '#8B5CF6', avgKey: 'avg_talk_ratio' },
];

const getDelta = (data) => {
  if (!data || data.length < 2) return null;
  const mid = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, mid);
  const secondHalf = data.slice(mid);
  const avgFirst = firstHalf.reduce((s, v) => s + v, 0) / firstHalf.length;
  const avgSecond = secondHalf.reduce((s, v) => s + v, 0) / secondHalf.length;
  return Math.round(avgSecond - avgFirst);
};

export default function SkillTrendsView({ performance, period = 'all' }) {
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        // Map dashboard period to the /performance/me/trends param.
        const p = period === 'weekly' ? 'weekly' : period === 'monthly' ? 'monthly' : 'all';
        const res = await fetch(`${API_URL}/api/performance/me/trends?period=${p}`, { credentials: 'include' });
        if (res.ok) setTrends(await res.json());
      } catch (e) { console.error(e); }
    };
    fetchTrends();
  }, [period]);

  return (
    <Card className="bg-card border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">Sales Skills Progress</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4">
          {SKILLS.map((skill) => {
            const data = trends?.[skill.key] || [];
            const current = data.length > 0 ? data[data.length - 1] : (skill.avgKey ? performance[skill.avgKey] : 0);
            const delta = getDelta(data);
            const val = Math.round(current);

            return (
              <div key={skill.key} className="flex-1" data-testid={`trend-card-${skill.key}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span style={{ color: skill.color }} className="font-medium">{skill.label}</span>
                  <span className="flex items-center gap-1">
                    <span className="font-bold" style={{ color: skill.color }}>{val}</span>
                    {delta !== null && (
                      <span className={`text-[10px] font-semibold ${delta >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {delta >= 0 ? '\u2191' : '\u2193'}{Math.abs(delta)}
                      </span>
                    )}
                  </span>
                </div>
                {data.length >= 2 ? (
                  <Sparkline data={data} color={skill.color} width={200} height={28} />
                ) : (
                  <Progress value={val} className="h-2 mt-1.5" />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
